import { coreContainer, coreProductItem, transferRequest } from "@tc/drizzle-v2"
import { inArray } from "drizzle-orm"
import {
  DBProductItem,
  ProductAttributes,
  TransferDoc,
  TransferRequestRow,
  TransferSpecificProductDetails,
} from "../types/core"

import {
  MessageAttributeValue,
  SQSClient,
  SendMessageCommand,
} from "@aws-sdk/client-sqs"
import { CoreStatus } from "@tc/graphql-server"
import { DbTransaction } from "@tc/middy-decorators"
import { extractProductFields } from "../utils/extract-product-fields"
import { defaultStatusLabel } from "../utils/status-label-mapper"
import {
  isTemplateOutdated,
  isTemplateOutdatedBatch,
} from "../utils/template-version-check"

const sqs_client = new SQSClient({})
const queue_url = process.env["QUEUE_URL"]

const extractId = (obj: unknown): string => {
  return obj && typeof obj === "object" && "id" in obj ? String(obj.id) : ""
}

const extractName = (obj: unknown): string => {
  return obj && typeof obj === "object" && "name" in obj ? String(obj.name) : ""
}

/**
 * Generate a sortable version of a certificate number by zero-padding
 * numeric segments so lexicographic sort matches natural numeric order.
 * e.g. "nzl2025/abc/92" → "nzl2025/abc/000092"
 */
const toCertificateNumberSort = (certificateNumber: string): string => {
  return certificateNumber
    .split("/")
    .map((segment) =>
      /^\d+$/.test(segment) ? segment.padStart(6, "0") : segment,
    )
    .join("/")
}

/**
 * Update accessible_by field based on party access matrix (User Story 118059)
 * AC2: Consignor can see all transfers where they are consignor
 * AC3: Consignee can see transfers in post-submission status (excludes 'Draft' and 'Resubmit')
 * AC4: 3rd party can see transfers in post-submission status (excludes 'Draft' and 'Resubmit')
 */
const updateAccessibleBy = (row: TransferRequestRow): string[] => {
  const status = row.status
  const accessible_by: string[] = []

  // AC2: Consignor access - all statuses
  accessible_by.push(extractId(row.consignorId))

  const isDraft = status === CoreStatus.Draft
  const isResubmit = status === CoreStatus.Resubmit

  // AC3: Consignee access - post-submission status (excludes Draft and Resubmit)
  if (!isDraft && !isResubmit && row.consigneeId) {
    accessible_by.push(extractId(row.consigneeId))
  }

  // AC4: 3rd party access - post-submission status (excludes Draft and Resubmit)
  if (
    !isDraft &&
    !isResubmit &&
    row.thirdPartyConsignor &&
    Array.isArray(row.thirdPartyConsignor)
  ) {
    row.thirdPartyConsignor.forEach((party) => {
      const partyId = extractId(party)
      if (partyId) accessible_by.push(partyId)
    })
  }

  // Remove duplicates and return
  return [...new Set(accessible_by)]
}

export const getTransferRequestData = async (
  tx: DbTransaction,
  id: string,
): Promise<TransferDoc | undefined> => {
  const result = await getTransferRequestBulkData(tx, [id])
  return result.get(id)
}

/**
 * Pure transform: row + products + templateOutdated → TransferDoc.
 * No DB calls — reusable by both single-ID and bulk paths.
 */
export const buildTransferRequestDoc = (
  row: TransferRequestRow,
  productsForDocument: TransferSpecificProductDetails[],
  templateOutdated: boolean,
): TransferDoc => {
  let departureDate: number | null = null
  let transportMode: string | null = null
  let localCarrier: string | null = null
  let carrierName: string | null = null
  let conveyanceReference: string | null = null

  if (row.transport) {
    try {
      const transportObj =
        typeof row.transport === "string"
          ? JSON.parse(row.transport)
          : row.transport
      const raw = transportObj?.departureDate
      transportMode = extractName(transportObj?.transportMode)
      localCarrier = transportObj?.localCarrier
      carrierName = transportObj?.carrierName
      conveyanceReference = transportObj?.conveyanceReference

      if (typeof raw === "string") {
        const date = new Date(raw)
        departureDate = isNaN(date.getTime()) ? null : date.getTime()
      } else if (typeof raw === "number") {
        departureDate = raw
      }
    } catch (e) {
      console.error("Failed to parse transport JSON", e)
    }
  }

  const productFields = extractProductFields(productsForDocument)
  const requiresAttention =
    templateOutdated &&
    ["RAISED", "RESUBMIT", "AMENDED"].includes(row.status || "")

  return {
    id: row.id,
    certificate_number: row.certificate_number,
    certificate_number_sort: toCertificateNumberSort(
      row.certificate_number ?? "",
    ),
    status: row.status,
    status_label: defaultStatusLabel.value(row.status as CoreStatus) ?? "",
    consignor_id: extractId(row.consignorId),
    consignee_id: extractId(row.consigneeId),
    third_party_consignor: Array.isArray(row.thirdPartyConsignor)
      ? row.thirdPartyConsignor.map(extractId).filter(Boolean)
      : [],
    third_party_consignor_count: Array.isArray(row.thirdPartyConsignor)
      ? row.thirdPartyConsignor.filter((party) => extractId(party)).length
      : 0,
    departure_date:
      departureDate === null
        ? null
        : new Date(departureDate).toString().replace(/\s*\(.*\)$/, ""),
    departure_ts: departureDate ?? null,
    updated_at: row.updatedAt?.toString().replace(/\s*\(.*\)$/, "") || null,
    accessible_by: updateAccessibleBy(row),
    unofficial_information: row.unofficialInformation || "",
    submitted_at: row.submittedAt?.toString().replace(/\s*\(.*\)$/, "") || null,
    approved_at: row.approvedAt?.getTime() || null,
    unofficial_commercial_info: row.unofficialInformation || null,
    product: productsForDocument.map((product) => ({
      id: product?.id || "",
      description: product?.description || "",
    })),
    product_descriptions: productFields.descriptions,
    product_net_weights: productFields.net_weights,
    product_net_volumes: productFields.net_volumes,
    product_container_numbers: productFields.container_numbers,
    product_NZHSC_codes: productFields.NZHSC_codes,
    product_eligibility_countries: productFields.eligibility_countries,
    product_seals: productFields.seals,
    product_productionBatch_ids: productFields.production_batch_ids,
    product_item_marks: productFields.item_marks,
    product_unofficial_commercial_ref: productFields.unofficial_commercial_ref,
    local_carrier: localCarrier,
    carrier_name: carrierName,
    conveyance_reference: conveyanceReference,
    destination_country: extractId(row.destination_country),
    transport_mode: transportMode || null,
    product_unofficial_commercial_info:
      productFields.unofficial_commercial_info,
    unique_consignment_ref: row.unofficialInformation ?? null,
    vessel: carrierName,
    sub_class: productFields.sub_class,
    requires_attention: requiresAttention,
    updated_ts: row.updatedAt
      ? new Date(row.updatedAt.toString()).getTime()
      : null,
    submitted_ts: row.submittedAt
      ? new Date(row.submittedAt.toString()).getTime()
      : null,
  }
}

/** Bulk fetch — batch queries for a chunk of IDs, returns Map<id, TransferDoc> */
export const getTransferRequestBulkData = async (
  tx: DbTransaction,
  ids: readonly string[],
): Promise<Map<string, TransferDoc>> => {
  if (ids.length === 0) return new Map()

  // 1. Batch fetch all transfer request rows
  const rows = await tx
    .select()
    .from(transferRequest)
    .where(inArray(transferRequest.id, ids as string[]))

  // 2. Batch fetch all product items for all productGroupIds
  const productGroupIds = rows
    .map((r) => r.productGroupId)
    .filter((id): id is string => id !== null)

  const allProductItems =
    productGroupIds.length > 0
      ? ((await tx
          .select({
            id: coreProductItem.id,
            productGroupId: coreProductItem.productGroupId,
            description: coreProductItem.description,
            attributes: coreProductItem.attributes,
            eligibilityCountries: coreProductItem.eligibilityCountries,
            packaging: coreProductItem.packaging,
            productReference: coreProductItem.productReference,
          })
          .from(coreProductItem)
          .where(
            inArray(coreProductItem.productGroupId, productGroupIds),
          )) as DBProductItem[])
      : []

  // Group products by productGroupId
  const productsByGroup = new Map<string, DBProductItem[]>()
  for (const p of allProductItems) {
    const pgId = p.productGroupId ?? ""
    const group = productsByGroup.get(pgId) ?? []
    group.push(p)
    productsByGroup.set(pgId, group)
  }

  // 3. Batch fetch all containers for all product items
  const allProductItemIds = allProductItems.map((p) => p.id)
  const allContainers =
    allProductItemIds.length > 0
      ? await tx
          .select({
            productItemId: coreContainer.productItemId,
            containerNumber: coreContainer.containerNumber,
            seals: coreContainer.seals,
          })
          .from(coreContainer)
          .where(inArray(coreContainer.productItemId, allProductItemIds))
      : []

  const containersByProduct = new Map<
    string,
    { containerNumber: string | null; seals: unknown[] }
  >()
  for (const c of allContainers) {
    if (c.productItemId)
      containersByProduct.set(c.productItemId, {
        containerNumber: c.containerNumber,
        seals: Array.isArray(c.seals) ? c.seals : [],
      })
  }

  // 4. Batch check template outdated (2 queries total instead of 2×N)
  const templateIds = [
    ...new Set(
      rows.map((r) => r.templateId).filter((id): id is string => id !== null),
    ),
  ]
  const templateOutdatedMap = await isTemplateOutdatedBatch(tx, templateIds)

  // 5. Build docs
  const result = new Map<string, TransferDoc>()

  for (const row of rows) {
    const products = row.productGroupId
      ? (productsByGroup.get(row.productGroupId) ?? [])
      : []

    const productsForDocument: TransferSpecificProductDetails[] = products.map(
      (product) => {
        const attributes: ProductAttributes =
          product?.attributes && typeof product.attributes === "object"
            ? product.attributes
            : {}
        const netWeight = attributes?.netWeight || null
        const netVolume = attributes?.netVolume || null
        const netWeightUnit =
          netWeight && typeof netWeight === "object" && netWeight.unit
            ? extractId(netWeight.unit)
            : ""
        const netVolumeUnit =
          netVolume && typeof netVolume === "object" && netVolume.unit
            ? extractId(netVolume.unit)
            : ""
        const NZHSCode = extractName(attributes?.NZHSCode) || null
        const productionBatchIds = Array.isArray(attributes?.productionBatchIds)
          ? attributes.productionBatchIds
          : []
        const productStatement =
          typeof attributes?.["productStatement"] === "string"
            ? attributes["productStatement"]
            : null
        const productReference =
          typeof product?.productReference === "string"
            ? product.productReference
            : null
        const itemMarks = Array.isArray(product?.packaging)
          ? (product.packaging as Array<{ itemMarksOuter?: string | null }>)
              .map((pkg) => pkg?.itemMarksOuter)
              .filter((m): m is string => Boolean(m) && typeof m === "string")
          : []
        const subClass = extractName(attributes?.["phytoSubClass"])
        const container = containersByProduct.get(product?.id || "")

        return {
          id: product?.id,
          description: extractName(product?.description),
          net_weight:
            netWeight && typeof netWeight === "object" && netWeight.value
              ? `${netWeight.value} ${netWeightUnit}`.trim()
              : null,
          net_volume:
            netVolume && typeof netVolume === "object" && netVolume.value
              ? `${netVolume.value} ${netVolumeUnit}`.trim()
              : null,
          eligibility_countries: Array.isArray(product?.eligibilityCountries)
            ? product.eligibilityCountries
                .flatMap((c: unknown) => {
                  if (typeof c === "string") return [c]
                  return [extractId(c), extractName(c)]
                })
                .filter(Boolean)
            : [],
          container_number: container?.containerNumber || null,
          seals: Array.isArray(container?.seals)
            ? (container.seals as string[])
            : [],
          NZHSC_code: NZHSCode,
          productionBatch_ids: productionBatchIds,
          item_marks: itemMarks,
          unofficial_commercial_ref: productReference,
          unofficial_commercial_info: productStatement,
          sub_class: subClass,
        }
      },
    )

    const templateOutdated = row.templateId
      ? (templateOutdatedMap.get(row.templateId) ?? false)
      : false

    result.set(
      row.id,
      buildTransferRequestDoc(row, productsForDocument, templateOutdated),
    )
  }

  return result
}

export const saveTransferRequestToSearch = async (
  tx: DbTransaction,
  id: string,
  collectionName: string,
  options?: { returnDoc?: boolean },
): Promise<void> => {
  const doc = await getTransferRequestData(tx, id)

  if (!doc || Object.keys(doc).length === 0) {
    console.error(`Failed to prepare document for id: ${id}`)
    return
  }

  if (options?.returnDoc) return doc as never

  const attributes: Record<string, MessageAttributeValue> = {
    collection: {
      StringValue: collectionName,
      DataType: "String",
    },
  }

  const command = new SendMessageCommand({
    QueueUrl: queue_url,
    MessageBody: JSON.stringify(doc),
    MessageAttributes: attributes,
  })

  await sqs_client.send(command)
}
